/**
 * Rule test-run dialog (#56).
 *
 * Lets the operator upload a local PDF/XML file and run the real extraction
 * pipeline as a dry-run with the unsaved rule draft appended to the active
 * rule set. The response is never persisted; the dialog only shows the
 * extracted preview + plausibility issues + raw JSON.
 */
import type { RuleDraft, TestRunRuleResponse } from '@invoice/shared/schemas';
import { AlertTriangle, CheckCircle2, FlaskConical, Loader2, Upload } from 'lucide-react';
import { useEffect, useId, useMemo, useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogCloseButton,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { useI18n } from '@/lib/i18n';
import {
  ACCEPTED_EXTENSIONS,
  ACCEPTED_MIME_TYPES,
  validateUploadFile,
} from '@/lib/queries/uploadInvoice';
import { useTestRunRule } from '@/lib/queries/rules';
import { cn } from '@/lib/utils';

interface RuleTestRunDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Draft used for the test run. The dialog stays a dumb consumer. */
  draft: RuleDraft | null;
}

export function RuleTestRunDialog({ open, onOpenChange, draft }: RuleTestRunDialogProps) {
  const { language } = useI18n();
  const copy =
    language === 'en'
      ? {
          title: 'Rule test run',
          description:
            'Upload a file and validate the extraction as a dry run with the active rule set plus the draft. Nothing is saved.',
          noDraft: 'No rule draft available. Fill in title and content in the edit form first.',
          fileLabel: 'Test file *',
          fileAria: 'Select test file',
          allowed: 'Allowed: PDF or XML, maximum 20 MB.',
          selected: 'Selected',
          invalid: 'File is invalid',
          failed: 'Test run failed',
          close: 'Close',
          startAria: 'Start test run',
          start: 'Start test run',
        }
      : {
          title: 'Regel-Testlauf',
          description:
            'Datei hochladen und die Extraktion als Dry-Run mit aktivem Regelwerk plus Entwurf prüfen. Es wird nichts gespeichert.',
          noDraft:
            'Kein Regel-Entwurf vorhanden. Erst Titel und Inhalt im Edit-Formular ausfüllen.',
          fileLabel: 'Testdatei *',
          fileAria: 'Testdatei auswählen',
          allowed: 'Erlaubt: PDF oder XML, maximal 20 MB.',
          selected: 'Ausgewählt',
          invalid: 'Datei ist ungültig',
          failed: 'Testlauf fehlgeschlagen',
          close: 'Schließen',
          startAria: 'Testlauf starten',
          start: 'Testlauf starten',
        };
  const titleId = useId();
  const fileFieldId = useId();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);

  const testRun = useTestRunRule();

  useEffect(() => {
    if (!open) {
      setSelectedFile(null);
      setFileError(null);
      testRun.reset();
    }
  }, [open, testRun]);

  const canRun = !!draft && !!selectedFile && !fileError && !testRun.isPending;

  const handleSelectFile = (file: File | null) => {
    setSelectedFile(file);
    testRun.reset();

    if (!file) {
      setFileError(null);
      return;
    }

    const validation = validateUploadFile(file);
    setFileError(validation.ok ? null : validation.message ?? copy.invalid);
  };

  const handleRun = async () => {
    if (!canRun || !draft || !selectedFile) return;
    try {
      await testRun.mutateAsync({ ruleDraft: draft, file: selectedFile });
    } catch {
      // surfaced via testRun.error
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange} ariaLabelledBy={titleId}>
      <DialogHeader className="flex-row items-start justify-between gap-4">
        <div className="flex flex-col gap-1">
          <DialogTitle id={titleId}>{copy.title}</DialogTitle>
          <DialogDescription>{copy.description}</DialogDescription>
        </div>
        <DialogCloseButton onClose={() => onOpenChange(false)} />
      </DialogHeader>

      <DialogBody>
        {!draft ? (
          <p
            role="alert"
            data-testid="rule-test-run-no-draft"
            className="rounded-md border border-dashed border-border px-3 py-2 text-sm text-muted-foreground"
          >
            {copy.noDraft}
          </p>
        ) : null}

        <div className="flex flex-col gap-1.5">
          <label htmlFor={fileFieldId} className="text-xs font-medium text-muted-foreground">
            {copy.fileLabel}
          </label>
          <Input
            id={fileFieldId}
            type="file"
            accept={[...ACCEPTED_MIME_TYPES, ...ACCEPTED_EXTENSIONS].join(',')}
            disabled={!draft || testRun.isPending}
            onChange={(event) => handleSelectFile(event.target.files?.[0] ?? null)}
            aria-label={copy.fileAria}
            data-testid="rule-test-run-file-input"
            className="cursor-pointer"
          />
          <p className="text-xs text-muted-foreground">
            {copy.allowed}
          </p>
          {selectedFile ? (
            <p className="text-xs text-foreground" data-testid="rule-test-run-file-name">
              {copy.selected}: {selectedFile.name}
            </p>
          ) : null}
          {fileError ? <span className="text-xs text-destructive">{fileError}</span> : null}
        </div>

        {testRun.isError ? (
          <div
            role="alert"
            data-testid="rule-test-run-error"
            className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive"
          >
            {copy.failed}: {testRun.error.message}
          </div>
        ) : null}

        {testRun.data ? <TestRunResult result={testRun.data} language={language} /> : null}
      </DialogBody>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
          {copy.close}
        </Button>
        <Button
          type="button"
          onClick={() => {
            void handleRun();
          }}
          disabled={!canRun}
          aria-label={copy.startAria}
          data-testid="rule-test-run-start"
        >
          {testRun.isPending ? (
            <Loader2 className="size-4 animate-spin" aria-hidden />
          ) : (
            <>
              <Upload className="size-4" aria-hidden />
              <FlaskConical className="size-4" aria-hidden />
            </>
          )}
          {copy.start}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

interface TestRunResultProps {
  result: TestRunRuleResponse;
  language: 'de' | 'en';
}

function TestRunResult({ result, language }: TestRunResultProps) {
  const copy =
    language === 'en'
      ? {
          valid: 'Plausible',
          errors: 'errors',
          status: 'Status',
          format: 'Format',
          warning: 'warning',
          warnings: 'warnings',
          invoiceNumber: 'Invoice number',
          supplier: 'Supplier',
          date: 'Date',
          net: 'Net',
          vat: 'VAT',
          gross: 'Gross',
          plausibility: 'Plausibility result',
          noIssues: 'No issues found.',
          preview: 'Response preview',
          locale: 'en-US',
        }
      : {
          valid: 'Plausibel',
          errors: 'Fehler',
          status: 'Status',
          format: 'Format',
          warning: 'Warnung',
          warnings: 'Warnungen',
          invoiceNumber: 'Rechnungsnummer',
          supplier: 'Lieferant',
          date: 'Datum',
          net: 'Netto',
          vat: 'MwSt',
          gross: 'Brutto',
          plausibility: 'Plausibilitätsergebnis',
          noIssues: 'Keine Probleme gefunden.',
          preview: 'Response-Preview',
          locale: 'de-DE',
        };
  const blob = result.blob as
    | {
        invoice?: { number?: string; issueDate?: string; currency?: string };
        seller?: { name?: string };
        totals?: { netAmount?: number; vatAmount?: number; grossAmount?: number };
      }
    | null
    | undefined;

  const errors = result.issues.filter((i) => i.level === 'error');
  const warnings = result.issues.filter((i) => i.level === 'warning');

  const totals = useMemo(() => {
    const t = blob?.totals ?? {};
    const currency = blob?.invoice?.currency ?? 'EUR';
    return {
      net: formatAmount(t.netAmount, currency, copy.locale),
      vat: formatAmount(t.vatAmount, currency, copy.locale),
      gross: formatAmount(t.grossAmount, currency, copy.locale),
    };
  }, [blob, copy.locale]);

  const responsePreview = useMemo(() => JSON.stringify(result, null, 2), [result]);

  return (
    <div className="flex flex-col gap-4" data-testid="rule-test-run-result">
      <div className="flex flex-wrap items-center gap-2">
        {result.valid ? (
          <Badge variant="success">
            <CheckCircle2 className="size-3" aria-hidden />
            {copy.valid}
          </Badge>
        ) : (
          <Badge variant="destructive">
            <AlertTriangle className="size-3" aria-hidden />
            {errors.length} {copy.errors}
          </Badge>
        )}
        <Badge variant="outline">{copy.status}: {result.status}</Badge>
        {result.format ? <Badge variant="outline">{copy.format}: {result.format}</Badge> : null}
        {warnings.length > 0 ? (
          <Badge variant="warning">
            {warnings.length} {warnings.length === 1 ? copy.warning : copy.warnings}
          </Badge>
        ) : null}
      </div>

      <dl className="grid grid-cols-1 gap-2 rounded-md border border-border bg-muted/20 px-3 py-2 text-xs sm:grid-cols-3">
        <Datum label={copy.invoiceNumber} value={blob?.invoice?.number ?? '—'} />
        <Datum label={copy.supplier} value={blob?.seller?.name ?? '—'} />
        <Datum label={copy.date} value={blob?.invoice?.issueDate ?? '—'} />
        <Datum label={copy.net} value={totals.net} />
        <Datum label={copy.vat} value={totals.vat} />
        <Datum label={copy.gross} value={totals.gross} />
      </dl>

      <div className="flex flex-col gap-2">
        <h3 className="text-sm font-medium text-foreground">{copy.plausibility}</h3>
        {result.issues.length === 0 ? (
          <p
            data-testid="rule-test-run-issues-empty"
            className="rounded-md border border-dashed border-border bg-muted/20 px-3 py-2 text-xs text-muted-foreground"
          >
            {copy.noIssues}
          </p>
        ) : (
          <ul className="flex flex-col gap-1" data-testid="rule-test-run-issues">
            {[...errors, ...warnings].map((issue) => (
              <li
                key={`${issue.level}-${issue.field}-${issue.code}`}
                className={cn(
                  'rounded-md border px-3 py-2 text-xs',
                  issue.level === 'error'
                    ? 'border-destructive/40 bg-destructive/5 text-destructive'
                    : 'border-amber-300 bg-amber-50 text-amber-900',
                )}
              >
                <p className="font-medium">{issue.code}</p>
                <p className="text-[11px] opacity-80">
                  {issue.field}: {issue.message}
                </p>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <h3 className="text-sm font-medium text-foreground">{copy.preview}</h3>
        <pre
          className="max-h-80 overflow-auto rounded-md border border-border bg-slate-950 p-3 text-xs text-slate-100"
          data-testid="rule-test-run-response-preview"
        >
          {responsePreview}
        </pre>
      </div>
    </div>
  );
}

interface DatumProps {
  label: string;
  value: string;
}

function Datum({ label, value }: DatumProps) {
  return (
    <div className="flex flex-col">
      <dt className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="font-mono text-foreground">{value}</dd>
    </div>
  );
}

function formatAmount(amount: number | undefined, currency: string, locale: string): string {
  if (typeof amount !== 'number' || Number.isNaN(amount)) return '—';
  try {
    return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(amount);
  } catch {
    return amount.toFixed(2);
  }
}
